import matrix from "~/components/Board/matrix";

export const initialDrawState = matrix.map((row) => row.map(() => false));

export function canMove(x, y, drawState, resultState) {
  // prevent already in result
  if (resultState.flat().some((res) => res.x === x && res.y == y)) {
    return false;
  }

  const { last } = drawState;

  // allow first point
  if (!last) {
    return true;
  }

  // prevent diagonal drawing
  const isDiagonal = last.x !== x && last.y !== y;
  const isNext = Math.abs(x - last.x) === 1 || Math.abs(y - last.y) === 1;

  if (isDiagonal || !isNext) {
    return false;
  }

  return true;
}

export function isMaxReached(drawState) {
  const count = drawState.reduce((total, items) => {
    total += items.filter((i) => i).length;
    return total;
  }, 0);

  return count >= 3;
}

export function isBidenWinner(result) {
  return result.filter((r) => matrix[r.x][r.y]).length >= 2;
}

export function getBidenOverallVotes(result) {
  return result.filter((r) => r.isBiden).length;
}

export function getTrumpOverallVotes(result) {
  return result.filter((r) => !r.isBiden).length;
}

export function isTrumpOverallWinner(result) {
  return getTrumpOverallVotes(result) >= 2;
}

export function getResult(drawState) {
  const res = drawState.reduce((result, items, x) => {
    items.forEach((item, y) => {
      if (item) {
        result.push({ x, y });
      }
    });

    return result;
  }, []);

  res.isBiden = isBidenWinner(res);

  return res;
}

function hasFreeNeighbour(point, free) {
  const neighbour = free.find((p) => {
    const isSame = p.x === point.x && p.y === point.y;
    const isNeighbour =
      (Math.abs(p.x - point.x) === 1 && p.y - point.y === 0) ||
      (Math.abs(p.y - point.y) === 1 && p.x - point.x === 0);

    return !isSame && isNeighbour;
  });

  return !!neighbour;
}

export function isImpossible(result) {
  const drawn = result.flat();

  const free = matrix.reduce((points, row, x) => {
    row.forEach((col, y) => {
      const isDrawn = !!drawn.find((d) => d.x === x && d.y === y);

      if (!isDrawn) {
        points.push({ x, y });
      }
    });

    return points;
  }, []);

  return free.some((point) => !hasFreeNeighbour(point, free));
}

export const allPossibleDrawStates = [
  [
    [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 2, y: 0 },
    ],
    [
      { x: 0, y: 1 },
      { x: 1, y: 1 },
      { x: 2, y: 1 },
    ],
    [
      { x: 0, y: 2 },
      { x: 1, y: 2 },
      { x: 2, y: 2 },
    ],
  ],
  [
    [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 1, y: 1 },
    ],
    [
      { x: 0, y: 1 },
      { x: 0, y: 2 },
      { x: 1, y: 2 },
    ],
    [
      { x: 2, y: 0 },
      { x: 2, y: 1 },
      { x: 2, y: 2 },
    ],
  ],
  [
    [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 2, y: 0 },
    ],
    [
      { x: 0, y: 1 },
      { x: 0, y: 2 },
      { x: 1, y: 2 },
    ],
    [
      { x: 1, y: 1 },
      { x: 2, y: 1 },
      { x: 2, y: 2 },
    ],
  ],
  [
    [
      { x: 0, y: 0 },
      { x: 0, y: 1 },
      { x: 0, y: 2 },
    ],
    [
      { x: 1, y: 0 },
      { x: 1, y: 1 },
      { x: 1, y: 2 },
    ],
    [
      { x: 2, y: 0 },
      { x: 2, y: 1 },
      { x: 2, y: 2 },
    ],
  ],
  [
    [
      { x: 0, y: 0 },
      { x: 0, y: 1 },
      { x: 1, y: 0 },
    ],
    [
      { x: 0, y: 2 },
      { x: 1, y: 1 },
      { x: 1, y: 2 },
    ],
    [
      { x: 2, y: 0 },
      { x: 2, y: 1 },
      { x: 2, y: 2 },
    ],
  ],
  [
    [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 2, y: 0 },
    ],
    [
      { x: 0, y: 1 },
      { x: 0, y: 2 },
      { x: 1, y: 1 },
    ],
    [
      { x: 1, y: 2 },
      { x: 2, y: 1 },
      { x: 2, y: 2 },
    ],
  ],
  [
    [
      { x: 0, y: 0 },
      { x: 0, y: 1 },
      { x: 1, y: 0 },
    ],
    [
      { x: 1, y: 1 },
      { x: 2, y: 0 },
      { x: 2, y: 1 },
    ],
    [
      { x: 0, y: 2 },
      { x: 1, y: 2 },
      { x: 2, y: 2 },
    ],
  ],
  [
    [
      { x: 0, y: 0 },
      { x: 0, y: 1 },
      { x: 1, y: 1 },
    ],
    [
      { x: 1, y: 0 },
      { x: 2, y: 0 },
      { x: 2, y: 1 },
    ],
    [
      { x: 0, y: 2 },
      { x: 1, y: 2 },
      { x: 2, y: 2 },
    ],
  ],
  [
    [
      { x: 0, y: 0 },
      { x: 0, y: 1 },
      { x: 0, y: 2 },
    ],
    [
      { x: 1, y: 0 },
      { x: 1, y: 1 },
      { x: 2, y: 0 },
    ],
    [
      { x: 1, y: 2 },
      { x: 2, y: 1 },
      { x: 2, y: 2 },
    ],
  ],
  [
    [
      { x: 0, y: 0 },
      { x: 0, y: 1 },
      { x: 0, y: 2 },
    ],
    [
      { x: 1, y: 0 },
      { x: 2, y: 0 },
      { x: 2, y: 1 },
    ],
    [
      { x: 1, y: 1 },
      { x: 1, y: 2 },
      { x: 2, y: 2 },
    ],
  ],
];
